import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Users as UsersIcon, Search, RefreshCw, Copy } from "lucide-react";

/**
 * Admin -> Users tab
 *
 * Shows all users in the database with filters (role, plan_type, search).
 * Auto-refreshes every 10 seconds for live sync.
 *
 * NOTE: The "Password" column displays the bcrypt hash exactly as stored.
 * bcrypt is a one-way hash and cannot be decoded back to the original
 * password — not by us, not by anyone. This is included per the product
 * requirement of "no decryption, stored format only".
 */
export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");        // "" | buyer | seller | admin
  const [planType, setPlanType] = useState(""); // "" | free | paid
  const [q, setQ] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const mounted = useRef(true);

  async function load(silent = false) {
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (role) params.role = role;
      if (planType) params.plan_type = planType;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/users", { params });
      if (!mounted.current) return;
      setItems(data.items || []);
      setLastRefresh(new Date());
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally { if (!silent) setLoading(false); }
  }

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role, planType]);
  // Debounced search
  useEffect(() => { const t = setTimeout(() => load(), 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  // Live sync — poll every 10s
  useEffect(() => {
    const t = setInterval(() => load(true), 10_000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [role, planType, q]);

  const counts = useMemo(() => ({
    total: items.length,
    buyer: items.filter(u => u.role === "buyer").length,
    seller: items.filter(u => u.role === "seller").length,
    paid: items.filter(u => u.plan_type === "paid").length,
    free: items.filter(u => u.plan_type === "free").length,
  }), [items]);

  function copy(text) {
    try { navigator.clipboard.writeText(text || ""); toast.success("Copied to clipboard"); } catch {}
  }

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-ink">Users</h2>
          <p className="text-sm text-muted-foreground">
            Every registered user in the database.{" "}
            {lastRefresh && <span className="text-xs">Auto-refresh every 10s · last {lastRefresh.toLocaleTimeString()}</span>}
          </p>
        </div>
        <button onClick={() => load()} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border text-sm hover:bg-slate-50" data-testid="users-refresh"><RefreshCw className="h-3.5 w-3.5" /> Refresh now</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        <Chip label="Total" value={counts.total} />
        <Chip label="Buyers" value={counts.buyer} tone="sky" />
        <Chip label="Sellers" value={counts.seller} tone="primary" />
        <Chip label="Free" value={counts.free} tone="slate" />
        <Chip label="Paid" value={counts.paid} tone="emerald" />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-2.5">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone" className="h-9 pl-8 pr-3 border border-border rounded-md text-sm min-w-[240px]" data-testid="users-search" />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} className="h-9 px-3 border border-border rounded-md text-sm bg-white" data-testid="users-role-filter">
          <option value="">All roles</option>
          <option value="buyer">Buyer</option>
          <option value="seller">Seller</option>
          <option value="admin">Admin</option>
        </select>
        <select value={planType} onChange={(e) => setPlanType(e.target.value)} className="h-9 px-3 border border-border rounded-md text-sm bg-white" data-testid="users-plan-filter">
          <option value="">All plans</option>
          <option value="free">Free</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl">
          <UsersIcon className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="mt-3 font-display text-lg font-semibold">No users match your filters</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2.5 font-semibold">Name</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Email</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Phone</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Role</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Plan</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Credits</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Used / Total</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Listing Limit</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Password (hash)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50/60" data-testid={`user-row-${u.id}`}>
                    <td className="px-3 py-2.5 font-medium">{u.name || "—"}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{u.email}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{u.phone || "—"}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border ${
                        u.role === "admin" ? "bg-primary/10 text-primary border-primary/20"
                        : u.role === "seller" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                        : "bg-sky-50 text-sky-800 border-sky-200"}`}>{u.role || "buyer"}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border ${
                        u.plan_type === "paid" ? "bg-amber-50 text-amber-800 border-amber-200"
                        : "bg-slate-100 text-slate-700 border-slate-200"}`}>{u.plan_type} {u.plan_type === "paid" && u.plan ? `· ${u.plan}` : ""}</span>
                    </td>
                    <td className="px-3 py-2.5 tabular-nums">{u.credits ?? 0}</td>
                    <td className="px-3 py-2.5 tabular-nums text-xs" data-testid={`usage-${u.id}`}>
                      {u.role === "admin" ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span>
                          <span className="font-semibold text-ink">{u.listingsUsed ?? 0}</span>
                          <span className="text-muted-foreground"> / {u.listingsLimit >= 10000 ? "\u221E" : u.listingsLimit}</span>
                          {u.listingsLimit < 10000 && (
                            <span className="ml-1 text-[10px] text-emerald-600 font-semibold">({u.listingsRemaining} left)</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <ListingLimitCell user={u} onSaved={(fresh) => setItems((arr) => arr.map((x) => x.id === u.id ? fresh : x))} />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <code className="text-[10px] font-mono bg-slate-100 px-1.5 py-0.5 rounded max-w-[220px] truncate inline-block" title={u.passwordHash}>{u.passwordHash || "—"}</code>
                        {u.passwordHash && (
                          <button onClick={() => copy(u.passwordHash)} className="h-6 w-6 rounded hover:bg-slate-200 flex items-center justify-center" title="Copy hash"><Copy className="h-3 w-3" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground mt-3">
        Passwords are stored as one-way <strong>bcrypt hashes</strong>. This is what the database physically holds — the original password cannot be recovered, only reset.
      </p>
    </div>
  );
}

function Chip({ label, value, tone = "slate" }) {
  const toneCls = {
    slate:  "bg-slate-100 text-slate-700",
    sky:    "bg-sky-50 text-sky-800",
    primary:"bg-primary/10 text-primary",
    emerald:"bg-emerald-50 text-emerald-800",
  }[tone] || "bg-slate-100";
  return (
    <div className={`rounded-lg px-3 py-2 ${toneCls}`}>
      <p className="text-[10px] uppercase font-semibold tracking-wider opacity-75">{label}</p>
      <p className="text-lg font-bold tabular-nums">{value}</p>
    </div>
  );
}

function ListingLimitCell({ user, onSaved }) {
  const isAdmin = user.role === "admin";
  const isOwner = (user.sellerType || "").toLowerCase() === "owner";
  // Every seller now gets 10 free listings by default (bumped from 7 for brokers /
  // ∞ for owners). Admin overrides + subscription top-ups persist to `listingLimit`.
  const defaultLimit = isAdmin ? "\u221E" : 10;
  const currentLimit = typeof user.listingLimit === "number" && user.listingLimit > 0
    ? user.listingLimit
    : (typeof user.customListingLimit === "number" && user.customListingLimit > 0 ? user.customListingLimit : null);
  const hasCustom = currentLimit != null;
  const [saving, setSaving] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(currentLimit ?? "");

  const save = async (nextVal) => {
    setSaving(true);
    try {
      const payload = nextVal === "" || nextVal == null ? { custom_listing_limit: null } : { custom_listing_limit: Number(nextVal) };
      const { data } = await api.patch(`/admin/users/${user.id}/listing-limit`, payload);
      onSaved?.(data.user);
      setEditing(false);
    } catch (e) {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  if (isAdmin) return <span className="text-xs text-muted-foreground">\u221E (admin)</span>;

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        <span className={`text-xs tabular-nums ${hasCustom ? "font-semibold text-primary" : "text-ink"}`}>
          {hasCustom ? currentLimit : defaultLimit}
        </span>
        <button onClick={() => { setVal(currentLimit ?? ""); setEditing(true); }} className="text-[10px] font-semibold text-primary hover:underline" data-testid={`edit-limit-${user.id}`}>
          Edit
        </button>
        {hasCustom && (
          <button onClick={() => save("")} className="text-[10px] font-semibold text-rose-600 hover:underline" data-testid={`reset-limit-${user.id}`}>
            Reset
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        min="0"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={String(defaultLimit)}
        className="w-16 h-7 text-xs px-1.5 rounded border border-border focus:outline-none focus:ring-2 focus:ring-primary/30"
        data-testid={`limit-input-${user.id}`}
      />
      <button disabled={saving} onClick={() => save(val)} className="text-[10px] font-semibold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 rounded px-1.5 py-1" data-testid={`save-limit-${user.id}`}>
        {saving ? "\u2026" : "Save"}
      </button>
      <button disabled={saving} onClick={() => setEditing(false)} className="text-[10px] text-muted-foreground hover:text-ink">Cancel</button>
    </div>
  );
}
